import { useCallback } from "react";

import {
  combineRunProps,
  parseErrorResponse,
  useRequestWithFeedback,
} from "../../../composites";
import { useObjectMemo } from "../../../hooks";
import { useLibrary } from "../../../providers";
import { REQUEST_STATUS } from "../../../composites/request/request.types";

import { API } from "./api";

import type {
  Data,
  ErrorResponse,
  OverrideRunProps,
} from "../../../composites/request/request.types";
import type { AnyException } from "../../../composites/request/request.helpers";

export const useFetchNachaPassthroughSubmission = () => {
  const { send, status, loading } =
    useRequestWithFeedback<API.NachaPassthrough.Submission>();

  const fetchSubmission = useCallback(
    (submissionId: API.NachaPassthrough.SubmissionId) =>
      send({
        action: API.fetchNachaPassthroughSubmission(submissionId),
      }),
    [send],
  );

  return useObjectMemo({
    send: fetchSubmission,
    loading,
    error: status === REQUEST_STATUS.error,
  });
};

export const useFetchNachaPassthroughEntries = () => {
  const { send, status, loading } =
    useRequestWithFeedback<API.NachaPassthrough.EntriesResponse>();

  const fetchEntries = useCallback(
    (
      submissionId: API.NachaPassthrough.SubmissionId,
      offset: number,
      limit: number,
      search?: string,
    ) =>
      send({
        action: API.fetchNachaPassthroughEntries(
          submissionId,
          offset,
          limit,
          search,
        ),
      }),
    [send],
  );

  return useObjectMemo({
    send: fetchEntries,
    loading,
    error: status === REQUEST_STATUS.error,
  });
};

export const useCancelNachaPassthroughSubmission = () => {
  const { send, status, loading } =
    useRequestWithFeedback<API.NachaPassthrough.Submission>();
  const l10n = useLibrary("translations");
  const { throwToast } = useLibrary("toasts");

  const cancelNachaPassthroughSubmission = useCallback(
    (
      submissionId: API.NachaPassthrough.SubmissionId,
      overrideRunProps: OverrideRunProps = {},
    ) => {
      send({
        action: API.cancelNachaPassthroughSubmission(submissionId),
        ...combineRunProps(
          {
            onSuccess: async () => {
              throwToast({
                kind: "success",
                message: l10n.getString(
                  "delete-payment-success-message",
                  null,
                  "Payment canceled.",
                ),
              });
            },
            onError: async (error: ErrorResponse<AnyException<Data>>) => {
              const { errors } = await parseErrorResponse(error);
              throwToast({
                kind: "error",
                message: errors.length
                  ? `${errors[0].description}`
                  : l10n.getString(
                      "delete-payment-error-message",
                      null,
                      "We could not cancel your payment. Please try again.",
                    ),
              });
            },
          },
          overrideRunProps,
        ),
      });
    },
    [send, l10n, throwToast],
  );

  return useObjectMemo({
    cancelNachaPassthroughSubmission,
    loading,
    error: status === REQUEST_STATUS.error,
  });
};
